import React from "react";
import { useFela } from "react-fela";
import useTranslation from "next-translate/useTranslation";

import { Grid } from "@/components/Grid";
import { useTheme } from "@/hooks/useTheme";
import { LogoProvider, ILogoProvider } from "@/components/LogoProvider";
import { Heading } from "@/components/Heading";

import { ICustomersLogosSection } from "./types";

type IProps = ICustomersLogosSection & JSX.IntrinsicElements["div"];

const logos: ILogoProvider[] = [
  {
    title: "Deloitte Digital",
    src: "/svgs/deloitte-digital.svg",
  },
  { title: "Walgreens", src: "/svgs/walgreens.svg" },
  { title: "IBM iX", src: "/svgs/ibm-ix.svg" },
  { title: "Purpose", src: "/svgs/purpose.svg" },
  { title: "Kroger", src: "/svgs/kroger.svg" },
];

export const CustomersLogosSection: React.FC<IProps> = ({
  title,
  isDarkMode = false,
  customStyle,
  ...rest
}) => {
  const { css } = useFela();
  const { bp, colors } = useTheme();
  const { t } = useTranslation("section/customersLogos");

  const bgColor = isDarkMode ? colors.CHARCOAL : "inherit";
  const titleColor = isDarkMode ? colors.WHITE : colors.DARK_GREY;
  const logoShadow = isDarkMode
    ? "none"
    : `20px 20px 80px ${colors.CETACEAN_BLUE}`;

  return (
    <Grid
      customStyle={{
        flexDirection: "column",
        ...customStyle,
      }}
      wrapperStyle={{ position: "relative", backgroundColor: bgColor }}
      id="CustomersLogosSection"
      {...rest}
    >
      <div
        className={css({
          position: "relative",
          zIndex: 1,
        })}
      >
        <div
          className={css({
            textAlign: "center",
          })}
        >
          <Heading as="h2" customStyle={{ color: titleColor }}>
            {title || t("section/customersLogos:title")}
          </Heading>
        </div>

        <div
          className={css({
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            [bp.FROM_DESKTOP]: {
              marginTop: 60,
              justifyContent: "space-around",
              flexWrap: "no-wrap",
            },
          })}
        >
          {logos.map((item, index) => (
            <LogoProvider
              key={`LogoProvider-${index}`}
              customStyle={{
                height: 80,
                boxShadow: logoShadow,
                backgroundColor: bgColor,
              }}
              {...item}
            />
          ))}
        </div>
      </div>
    </Grid>
  );
};
